import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import QueryTemplateManager from './QueryTemplates/QueryTemplateManager';
import AuthModal from './Auth/AuthModal';
import { ArrowRight } from 'lucide-react';
import ErrorBoundary from './UI/ErrorBoundary';
import 'flag-icons/css/flag-icons.min.css';

// Main component now shows Query Template Manager for authenticated users
const AISearchRankingAnalyzerContent = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  return (
    <div className="h-full">
      {/* Show Query Template Manager for authenticated users, Auth Landing for non-authenticated */}
      {isAuthenticated ? (
        <ErrorBoundary
          title={t('aiSearch.errors.queryTemplateTitle')}
          customMessage={t('aiSearch.errors.queryTemplateMessage')}
        >
          <QueryTemplateManager />
        </ErrorBoundary>
      ) : (
        <AuthLandingPage />
      )}
    </div>
  );
};

// Modern Landing Page with Floating Header
const AuthLandingPage = () => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState('login');
  const { toggleTheme, isDark } = useTheme();

  const handleStartForFree = () => {
    setAuthModalView('register');
    setShowAuthModal(true);
  };

  const handleLogin = () => {
    setAuthModalView('login');
    setShowAuthModal(true);
  };

  return (
    <div className="relative min-h-screen bg-[#f3f2f1]">
      {/* Floating Header - Properly centered with 12px radius */}
      <header className="fixed top-8 left-0 right-0 z-50 flex justify-center" dir="ltr">
        <nav className="bg-[#242323] rounded-xl px-2 py-1.5 flex items-center justify-between shadow-lg w-auto">
          {/* Logo only */}
          <div className="flex items-center pr-2.5">
            {/* Using Brandon logo */}
            <img
              src="/brandon-logo.svg"
              alt="Brandon"
              className="h-9 w-auto"
            />
          </div>

          {/* Navigation Links - About and Platform */}
          <div className="hidden md:flex items-center space-x-3.5 px-2.5">
            <a href="#about" className="text-white hover:text-[#d8f9b8] transition-colors text-sm font-medium" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>About</a>
            <a href="#platform" className="text-white hover:text-[#d8f9b8] transition-colors text-sm font-medium" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>Platform</a>
          </div>

          {/* Auth Buttons */}
          <div className="flex items-center space-x-1.5 pl-2.5">
            <button
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="text-white/80 hover:text-[#d8f9b8] transition-colors p-1.5"
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="5" />
                  <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            <button
              onClick={handleLogin}
              className="text-white hover:text-[#d8f9b8] transition-colors text-sm font-medium px-2 py-0.5"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Login
            </button>
            <button
              onClick={handleStartForFree}
              className="bg-[#d8f9b8] text-[#242323] px-4 py-2.5 rounded-[7px] text-sm font-semibold hover:bg-[#c8e9a8] transition-colors"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Start for free
            </button>
          </div>
        </nav>
      </header>

      {/* Hero Section - Two Column Layout */}
      <div className="min-h-screen flex">
        <div className="w-full grid grid-cols-1 lg:grid-cols-3">

            {/* Left Column - Hero Content - Takes 2/3 */}
            <div className="lg:col-span-2 flex items-center justify-center px-6 lg:px-12 pt-32 pb-12 lg:py-0">
              <div className="space-y-8 text-left max-w-2xl">

                {/* Country Flags - Moved above title */}
                <div className="flex items-center gap-3 animate-bump-in">
                  <span
                    className="fi fi-de rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Germany"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-il rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Israel"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-it rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Italy"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-fr rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="France"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-es rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Spain"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-nl rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Netherlands"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-pt rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Portugal"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-ua rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Ukraine"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                  <span
                    className="fi fi-dk rounded-sm shadow-sm hover:scale-110 transition-transform cursor-pointer"
                    title="Denmark"
                    style={{
                      fontSize: '22px',
                      display: 'inline-block',
                      lineHeight: '1',
                      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                    }}
                  ></span>
                </div>

                <div className="space-y-6">
                  <h1 className="text-5xl lg:text-6xl font-normal leading-[1.1] text-[#2b2d2d] animate-bump-in animation-delay-100" style={{
                  fontSize: 'clamp(5rem, 8vw, 6rem)',
                  fontFamily: '"Bricolage Grotesque", sans-serif',
                  fontOpticalSizing: 'auto',
                  fontWeight: 600
                }}>
                  Win Local Markets in<br />
                  AI Search Results
                </h1>

                  <p className="text-lg text-[#666666] max-w-lg animate-bump-in animation-delay-200" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    The only platform that optimizes your visibility for region-specific queries in any language.
                  </p>
                </div>

                {/* CTA Button - Left Aligned */}
                <div className="animate-bump-in animation-delay-300">
                  <div className="relative inline-block group">
                    {/* Rotating glow border */}
                    <div
                      className="absolute -inset-1 rounded-lg opacity-75 blur-sm"
                      style={{
                        background: 'linear-gradient(45deg, transparent 30%, #d8f9b8 50%, transparent 70%)',
                        animation: 'rotate 3s linear infinite',
                      }}
                    />

                    {/* Secondary glow for enhanced effect */}
                    <div
                      className="absolute -inset-1 rounded-lg opacity-50"
                      style={{
                        background: 'linear-gradient(-45deg, transparent 30%, #c8e9a8 50%, transparent 70%)',
                        animation: 'rotate 3s linear infinite reverse',
                      }}
                    />

                    {/* Button container with border */}
                    <div
                      className="relative rounded-lg p-[2px]"
                      style={{
                        background: 'linear-gradient(45deg, #d8f9b8, #c8e9a8, #d8f9b8, #c8e9a8)',
                        backgroundSize: '300% 300%',
                        animation: 'gradient-shift 3s ease infinite'
                      }}
                    >
                      {/* Actual button */}
                      <button
                        onClick={handleStartForFree}
                        className="relative bg-[#2b2d2d] text-white px-8 py-3 rounded-lg font-semibold text-base hover:bg-[#1b1d1d] transition-all duration-300 inline-flex items-center gap-3 w-full"
                        style={{
                          fontFamily: '"Space Grotesk", sans-serif',
                        }}
                      >
                        Invited?
                        <ArrowRight className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Add CSS animations */}
                  <style>{`
                    @keyframes rotate {
                      0% {
                        transform: rotate(0deg);
                      }
                      100% {
                        transform: rotate(360deg);
                      }
                    }

                    @keyframes gradient-shift {
                      0% {
                        background-position: 0% 50%;
                      }
                      50% {
                        background-position: 100% 50%;
                      }
                      100% {
                        background-position: 0% 50%;
                      }
                    }

                    .group:hover > div:first-child,
                    .group:hover > div:nth-child(2) {
                      animation-duration: 1.5s;
                      opacity: 1;
                    }

                    .group:hover > div:nth-child(3) {
                      animation-duration: 1.5s;
                    }
                  `}</style>
                </div>
              </div>
            </div>

            {/* Right Column - Data Visualizations - Takes 1/3 */}
            <div className="lg:col-span-1 bg-[#d8f9b8] flex flex-col justify-center px-6 lg:px-8 py-12 lg:py-0 animate-bump-in animation-delay-300">
              <div className="w-full max-w-sm mx-auto">
                {/* Column Title */}
                <div className="mb-6">
                  <div style={{
                    fontFamily: '"Bricolage Grotesque", sans-serif',
                    fontWeight: 600,
                    fontSize: '32px',
                    lineHeight: '1.2',
                    color: '#2b2d2d',
                    marginBottom: '12px',
                    display: 'block',
                    maxWidth: '100%'
                  }}>
                    Your Customers Are Already There
                  </div>
                  <p style={{
                    fontFamily: '"Space Grotesk", sans-serif',
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: 'rgba(43, 45, 45, 0.7)',
                    display: 'block'
                  }}>
                    Stats prove that zero-click search results in AI engines become the norm.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Data Card 1 - Traffic Growth */}
                <div className="rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1" style={{ backgroundColor: '#ffffff' }}>
                  <div className="text-3xl lg:text-4xl font-bold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}>
                    527%
                  </div>
                  <div className="text-sm font-semibold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    Traffic Growth
                  </div>
                  <div className="text-[10px] text-[#999999] italic" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    ¹Search Engine Land, 2025
                  </div>
                </div>

                {/* Data Card 2 - User Growth */}
                <div className="rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1" style={{ backgroundColor: '#ffffff' }}>
                  <div className="text-3xl lg:text-4xl font-bold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}>
                    120%
                  </div>
                  <div className="text-sm font-semibold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    YoY User Growth
                  </div>
                  <div className="text-[10px] text-[#999999] italic" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    ²Wix & SEranking, 2025
                  </div>
                </div>

                {/* Data Card 3 - Market Share */}
                <div className="rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1" style={{ backgroundColor: '#ffffff' }}>
                  <div className="text-3xl lg:text-4xl font-bold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Bricolage Grotesque", sans-serif' }}>
                    8.2%
                  </div>
                  <div className="text-sm font-semibold text-[#2b2d2d] mb-1" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    Market Share
                  </div>
                  <div className="text-[10px] text-[#999999] italic" style={{ fontFamily: '"Space Grotesk", sans-serif' }}>
                    ³Statista & SEranking, 2025
                  </div>
                </div>
                </div>

              </div>
            </div>

          </div>
        </div>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialView={authModalView}
        />
      )}
    </div>
  );
};

const AISearchRankingAnalyzerRefactored = () => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary
      title={t('aiSearch.errors.applicationTitle')}
      customMessage={t('aiSearch.errors.applicationMessage')}
    >
      <AISearchRankingAnalyzerContent />
    </ErrorBoundary>
  );
};

export default AISearchRankingAnalyzerRefactored;